import { useMemo } from 'react';
import { type CustomerQuote, getQuoteLegQuantity, Quote, QuoteStatusEnum } from '../../types';
import { InlineFormattedNumber } from '../FormattedNumber';
import { Table, Tbody, Td, Th, Thead, Tr } from '../Table';
import { Text } from '../Text';

/**
 * Simple component just listing the fixings applied to some entity, rendering a list with the different prices
 * Highly coupled to Quote | CustomerQuote currently. Should be decoupled in the future for better reuse
 */
export const FixingSummary = ({ entity }: { entity: Quote | CustomerQuote }) => {
  const fixingLines: JSX.Element[] = useMemo(() => {
    if (entity.FixingDetails?.Index) {
      // standard entry, we can just grab the details from this top-level fixing details blob
      return [
        <FixingLine
          key={entity.FixingDetails.Index}
          quantity={entity.TradedQty}
          currency={entity.Currency}
          index={entity.FixingDetails.Index}
          fixing={entity.FixingDetails.Fixing}
        />,
      ];
    } else {
      // multi-legged entity. Grab fixing details from the legs
      // For taker (Quote entity), you have multiple markets, n legs per market, so only grab the legs for the market we filled at
      // For maker (CustomerQuote), there is only one market (implied), so just do all legs
      const legs =
        entity instanceof Quote
          ? entity.Legs?.filter(
              leg => leg.QuoteStatus === QuoteStatusEnum.Filled || leg.QuoteStatus === QuoteStatusEnum.PendingFix
            )
          : entity.LegSummary;

      if (!legs) {
        return [];
      }

      return legs.map(leg => (
        <FixingLine
          key={leg.Symbol}
          currency={leg.Currency}
          quantity={leg.TradedQty ?? getQuoteLegQuantity(entity.Side, leg)}
          index={leg.FixingDetails?.Index}
          fixing={leg.FixingDetails?.Fixing}
        />
      ));
    }
  }, [entity]);

  return (
    <Table w="100%">
      <Thead>
        <Tr>
          <Th width="40%">Index</Th>
          <Th align="right" width="25%">
            Qty
          </Th>
          <Th align="right" width="35%">
            Fixing
          </Th>
        </Tr>
      </Thead>

      <Tbody>{fixingLines}</Tbody>
    </Table>
  );
};

const FixingLine = ({
  quantity,
  currency,
  index,
  fixing,
}: {
  quantity: string | undefined;
  currency: string | undefined;
  index: string | undefined;
  fixing: string | undefined;
}) => {
  return (
    <Tr gap="spacing4" alignSelf="flex-start">
      <Td>
        <Text color="colorTextSubtle">{index}</Text>
      </Td>

      <Td align="right">
        <InlineFormattedNumber number={quantity} currency={currency} />
      </Td>

      {/* USD is hardcoded until each FixingIndex has a QuoteCurrency on it */}
      <Td align="right">{fixing ? <InlineFormattedNumber number={fixing} currency="USD" truncate={false} /> : '-'}</Td>
    </Tr>
  );
};
